import { useState } from 'react';
import { supabase } from '../lib/supabase';
import { Negotiator } from '../types/negotiator';
import { buildRetrocessionRowAmounts } from '../utils/retrocessions';

export function useNegotiatorStats() {
  const [negotiators, setNegotiators] = useState<Negotiator[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadData = async (startDate: string, endDate: string) => {
    try {
      console.log('Loading transactions with params:', {
        startDate,
        endDate
      });

      // Récupérer l'année fiscale à partir de la date de début
      const startDateObj = new Date(startDate);
      const endDateObj = new Date(endDate);
      const fiscalYear = startDateObj.getMonth() + 1 < 4 
        ? startDateObj.getFullYear() - 1 
        : startDateObj.getFullYear();

      console.log('📅 [useNegotiatorStats] Period:', { startDate, endDate, fiscalYear });

      // Récupérer tous les négociateurs actifs avec leurs objectifs
      const { data: negotiatorsData, error: negotiatorsError } = await supabase
        .from('negotiators')
        .select(`
          id,
          firstname,
          lastname,
          role,
          status,
          archived,
          arrival_date,
          departure_date,
          has_custom_objectives,
          monthly_objectives,
          negotiator_annual_objectives!left (
            fiscal_year,
            annual_objective
          )
        `)
        .eq('archived', false)
        .order('lastname');

      if (negotiatorsError) throw negotiatorsError;

      // Filtrer les négociateurs selon leurs dates d'arrivée et de départ
      const filteredNegotiators = negotiatorsData.filter(negotiator => {
        const startDateObj = new Date(startDate);
        const endDateObj = new Date(endDate);
        
        // Si le négociateur a une date d'arrivée, vérifier qu'elle est avant ou pendant la période
        if (negotiator.arrival_date) {
          const arrivalDate = new Date(negotiator.arrival_date);
          if (arrivalDate > endDateObj) {
            return false; // Le négociateur n'était pas encore arrivé pendant cette période
          }
        }
        
        // Si le négociateur a une date de départ, vérifier qu'elle est après ou pendant la période
        if (negotiator.departure_date) {
          const departureDate = new Date(negotiator.departure_date);
          if (departureDate < startDateObj) {
            return false; // Le négociateur était déjà parti avant cette période
          }
        }
        
        return true;
      });

      // Récupérer toutes les transactions
      const { data: transactionsData, error: transactionsError } = await supabase
        .from('transactions')
        .select(`
          id,
          fees_without_tax,
          status,
          compromise_date,
          transaction_retrocessions (
            id,
            amount,
            type,
            primary_negotiator_id,
            secondary_negotiator_id,
            tertiary_negotiator_id,
            quaternary_negotiator_id,
            distribution_type,
            distribution_percentages
          ),
          transaction_negotiators (
            negotiator_id,
            role,
            percentage,
            agency,
            negotiator:negotiators!inner (
              id,
              firstname,
              lastname,
              role,
              archived,
              departure_date
            )
          )
        `)
        .or(`and(status.eq.potentiel),and(status.in.(compromis,acte,encaisse,cloture),compromise_date.gte.${startDate},compromise_date.lte.${endDate})`);

      if (transactionsError) throw transactionsError;

      // Calculer les statistiques pour chaque négociateur
      const negotiatorStats = new Map<string, {
        total: number;
        potential: number;
        count: number;
        vendeur_count: number;
        vendeur_apporteur_count: number;
        apporteur_count: number;
        autre_count: number;
      }>();

      // Initialiser les statistiques pour chaque négociateur
      filteredNegotiators.forEach(negotiator => {
        negotiatorStats.set(negotiator.id, {
          total: 0,
          potential: 0,
          count: 0,
          vendeur_count: 0,
          vendeur_apporteur_count: 0,
          apporteur_count: 0,
          autre_count: 0
        });
      });

      transactionsData.forEach(transaction => {
        // Part de rétrocession par ligne : un négociateur présent dans deux agences
        // a deux lignes, la rétrocession ne doit être déduite qu'une fois au total
        const retrocessionsPerRow = buildRetrocessionRowAmounts(
          transaction.transaction_retrocessions,
          transaction.transaction_negotiators || []
        );

        // Traiter chaque négociateur de la transaction
        transaction.transaction_negotiators?.forEach((negotiator, rowIndex) => {
          // Calculer la part du négociateur sur les honoraires bruts
          const baseAmount = (transaction.fees_without_tax * negotiator.percentage) / 100;
          
          // Soustraire les rétrocessions attribuées à ce négociateur
          const retrocessionAmount = retrocessionsPerRow[rowIndex] || 0;
          const netAmount = baseAmount - retrocessionAmount;

          const current = negotiatorStats.get(negotiator.negotiator_id) || {
            total: 0,
            potential: 0,
            count: 0,
            vendeur_count: 0,
            vendeur_apporteur_count: 0,
            apporteur_count: 0,
            autre_count: 0
          };

          // Ne compter les transactions que si elles ne sont pas en potentiel
          if (transaction.status !== 'potentiel') {
            if (negotiator.role === 'vendeur') {
              current.vendeur_count++;
            } else if (negotiator.role === 'vendeur_apporteur') {
              current.vendeur_apporteur_count++;
              current.vendeur_count++;
              current.apporteur_count++;
            } else if (negotiator.role === 'apporteur') {
              current.apporteur_count++;
            } else {
              current.autre_count++;
            }
          }

          if (transaction.status === 'potentiel') {
            current.potential += netAmount;
          } else {
            current.total += netAmount;
            current.count++;
          }

          negotiatorStats.set(negotiator.negotiator_id, current);
        });
      });

      // Fonction pour calculer l'objectif pour une période donnée
      const calculatePeriodObjective = (negotiator: any) => {
        let periodObjective = 0;

        console.log(`\n🎯 [calculatePeriodObjective] Negotiator: ${negotiator.firstname} ${negotiator.lastname}`);
        console.log('  - has_custom_objectives:', negotiator.has_custom_objectives);
        console.log('  - monthly_objectives:', negotiator.monthly_objectives);

        // Si objectifs mensuels personnalisés
        if (negotiator.has_custom_objectives && negotiator.monthly_objectives?.[fiscalYear]) {
          const monthlyObjectives = negotiator.monthly_objectives[fiscalYear];
          console.log('  - Using custom monthly objectives for year:', fiscalYear);
          console.log('  - Monthly objectives:', monthlyObjectives);
          
          // Calculer les mois inclus dans la période
          const periodStart = new Date(startDateObj);
          const periodEnd = new Date(endDateObj);
          
          console.log('  - Period start:', periodStart.toISOString());
          console.log('  - Period end:', periodEnd.toISOString());
          
          // Parcourir tous les mois de la période
          let currentMonth = new Date(periodStart.getFullYear(), periodStart.getMonth(), 1);
          const lastMonth = new Date(periodEnd.getFullYear(), periodEnd.getMonth(), 1);
          
          while (currentMonth <= lastMonth) {
            const monthKey = String(currentMonth.getMonth() + 1).padStart(2, '0');
            const monthObjective = monthlyObjectives[monthKey] || 0;
            periodObjective += monthObjective;
            
            console.log(`  - Month ${monthKey}: ${monthObjective} € (total: ${periodObjective} €)`);
            
            currentMonth.setMonth(currentMonth.getMonth() + 1);
          }
        } else {
          // Sinon utiliser l'objectif annuel standard et le proratiser
          const annualObjective = negotiator.negotiator_annual_objectives?.find(
            (obj: any) => obj.fiscal_year === fiscalYear
          );
          const fullAnnualObjective = annualObjective?.annual_objective || 0;
          
          console.log('  - Using standard annual objective:', fullAnnualObjective);
          
          // Calculer le nombre de mois dans la période
          const monthsDiff = (endDateObj.getFullYear() - startDateObj.getFullYear()) * 12 
            + (endDateObj.getMonth() - startDateObj.getMonth()) + 1;
          
          console.log('  - Months in period:', monthsDiff);
          
          // Proratiser l'objectif annuel
          periodObjective = (fullAnnualObjective / 12) * monthsDiff;
          
          console.log('  - Prorated objective:', periodObjective);
        }

        console.log('  ✅ Final period objective:', periodObjective);
        return periodObjective;
      };

      // Combiner les données
      const formattedData = filteredNegotiators.map(negotiator => {
        const stats = negotiatorStats.get(negotiator.id) || {
          total: 0,
          potential: 0,
          count: 0,
          vendeur_count: 0,
          vendeur_apporteur_count: 0,
          apporteur_count: 0,
          autre_count: 0
        };

        // Calculer l'objectif pour la période
        const periodObjective = calculatePeriodObjective(negotiator);

        const total_sales = Math.round(stats.total * 100) / 100;
        const potential_sales = Math.round(stats.potential * 100) / 100;
        const completion_rate = periodObjective ? (total_sales / periodObjective) * 100 : 0;
        
        return {
          ...negotiator,
          // La part "Direction" (EKYO) est isolée dans son propre statut
          // pour ne pas être mélangée avec les négociateurs/directeurs.
          status: negotiator.firstname === 'DIRECTION' ? 'direction' : negotiator.status,
          total_sales,
          potential_sales,
          sales_count: stats.count,
          completion_rate,
          remaining: periodObjective - total_sales,
          vendeur_count: stats.vendeur_count,
          vendeur_apporteur_count: stats.vendeur_apporteur_count,
          apporteur_count: stats.apporteur_count,
          autre_count: stats.autre_count,
          annual_objective: periodObjective
        };
      });

      // Trier par CA réalisé
      const sortedData = formattedData.sort((a, b) => b.total_sales - a.total_sales);
      setNegotiators(sortedData);
    } catch (err: any) {
      console.error('Error in useNegotiatorStats:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return { negotiators, loading, error, loadData };
}